import {TFunction} from 'i18next';
import {boolean, object, SchemaOf, string} from 'yup';
import {EnvironmentType} from '../../../../shared/model/EnvironmentType.model';
import {
  envFormInitValues,
  envFormValidationSchema,
  EnvironmentFormFields,
  EnvironmentsFormFields
} from '../Environment.form.model';
import {Environment, EnvironmentAttributes} from '../Environment.model';
import {findEnvironmentByType} from '../EnvironmentUtils';

export type CustomEnvironmentAttributes = EnvironmentAttributes & {
  successUrl?: string;
  cancelUrl?: string;
  failureUrl?: string;
  callbackUrl?: string;
  externalStatusPageEnabled: boolean;
  externalStatusPageUrl?: string;
};

export type CustomEnvironmentFormFields = EnvironmentFormFields & {
  successUrl: string;
  cancelUrl: string;
  failureUrl: string;
  callbackUrl: string;
  externalStatusPageEnabled: boolean;
  externalStatusPageUrl?: string;
};

const customEnvFormInitValues = (
  environment: Environment<CustomEnvironmentAttributes>
): CustomEnvironmentFormFields => ({
  ...envFormInitValues(environment),
  externalStatusPageEnabled: environment.attributes.externalStatusPageEnabled || false,
  externalStatusPageUrl: environment.attributes.externalStatusPageUrl ?? '',
  successUrl: environment.attributes.successUrl ?? '',
  failureUrl: environment.attributes.failureUrl ?? '',
  cancelUrl: environment.attributes.cancelUrl ?? '',
  callbackUrl: environment.attributes.callbackUrl ?? ''
});

export const customEnvsFormInitValues = (environments: Array<Environment<CustomEnvironmentAttributes>>): EnvironmentsFormFields<CustomEnvironmentFormFields> => {
  const productionEnv = findEnvironmentByType(EnvironmentType.PRODUCTION, environments);
  const sandboxEnv = findEnvironmentByType(EnvironmentType.SANDBOX, environments);
  return {
    ...(productionEnv && {PRODUCTION: customEnvFormInitValues(productionEnv)}),
    ...(sandboxEnv && {SANDBOX: customEnvFormInitValues(sandboxEnv)})
  };
};

const customEnvFormValidationSchema = (t: TFunction): SchemaOf<CustomEnvironmentFormFields> =>
  object().shape({
    ...envFormValidationSchema.fields,
    externalStatusPageEnabled: boolean()
      .required(t('SHARED.FIELD_REQUIRED')),
    externalStatusPageUrl: string()
      .url('Provide valid URL')
      .when('externalStatusPageEnabled', {
        is: true,
        then: string().required(t('SHARED.FIELD_REQUIRED'))
      }),
    successUrl: string()
      .url('Provide valid URL')
      .when('externalStatusPageEnabled', {
        is: false,
        then: string().required(t('SHARED.FIELD_REQUIRED'))
      }),
    failureUrl: string()
      .url('Provide valid URL')
      .when('externalStatusPageEnabled', {
        is: false,
        then: string().required(t('SHARED.FIELD_REQUIRED'))
      }),
    cancelUrl: string()
      .url('Provide valid URL')
      .when('externalStatusPageEnabled', {
        is: false,
        then: string().required(t('SHARED.FIELD_REQUIRED'))
      }),
    callbackUrl: string()
      .required(t('SHARED.FIELD_REQUIRED'))
      .url('Provide valid URL')
  }).defined();

export const customEnvsFormValidationSchema = (t: TFunction): SchemaOf<EnvironmentsFormFields<CustomEnvironmentFormFields>> =>
  object().shape({
    PRODUCTION: customEnvFormValidationSchema(t),
    SANDBOX: customEnvFormValidationSchema(t)
  }).defined();
